import { useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { AutoCompleteCheckbox } from "../../../subcomponents/AutoComplete";
import { Insurance } from "../../../types";
import { Colors } from "../../../Colors";

interface InsuranceAcceptedSelectProps {
  value: Insurance[];
  onChange: (insurances: Insurance[]) => void;
  options: Insurance[];
  label?: string;
  placeholder?: string;
}

export default function InsuranceAcceptedSelect({
  value,
  onChange,
  options,
  label = "Insurances Accepted",
  placeholder = "Select insurance plans",
}: InsuranceAcceptedSelectProps) {
  const insuranceSelectOptions = useMemo(
    () =>
      options.map((insurance) => ({
        label: insurance.name,
        value: insurance.insuranceId,
      })),
    [options]
  );

  const selectedOptions = useMemo(
    () =>
      value
        .map((insurance) => {
          const option = options.find(
            (opt) => opt.insuranceId === insurance.insuranceId
          );
          return option
            ? { label: option.name, value: option.insuranceId }
            : null;
        })
        .filter(
          (opt): opt is { label: string; value: string } => opt !== null
        ),
    [value, options]
  );

  const allInsurancesSelected =
    insuranceSelectOptions.length > 0 &&
    selectedOptions.length === insuranceSelectOptions.length;

  const handleToggleAllInsurances = () => {
    onChange(allInsurancesSelected ? [] : options);
  };

  const handleChange = (
    newSelectedOptions: { label: string; value: string }[]
  ) => {
    const selectedInsurances = options.filter((option) =>
      newSelectedOptions.some(
        (selected) => selected.value === option.insuranceId
      )
    );
    onChange(selectedInsurances);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mb: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        gap="10px"
      >
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ marginBottom: "8px", color: Colors.coolWhite }}
        >
          {label}
        </Typography>
        <Button
          variant="text"
          color="primary"
          onClick={handleToggleAllInsurances}
          sx={{
            width: "fit-content",
            textTransform: "none",
            alignSelf: "flex-end",
          }}
        >
          {allInsurancesSelected ? "Deselect All" : "Select All"}
        </Button>
      </Box>
      {insuranceSelectOptions.length > 0 && (
        <AutoCompleteCheckbox
          label=""
          placeholder={placeholder}
          options={insuranceSelectOptions}
          value={selectedOptions}
          setValue={handleChange}
        />
      )}
    </Box>
  );
}
